import React, { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { List } from "../../../types";
import AnimatePresence from "../../ui/AnimatePresence";
import { ListPopover } from "../ListPopover";
import { DEFAULT_LIST_EMOJI } from "../../../constants/defaults";
import { useSharedListIds } from "../../../hooks/queries/useSharedListIds";
import SharedListMark from "../../ui/SharedListMark";
import Tooltip from "../../ui/Tooltip";

export interface ListPickerProps {
  lists: List[];
  selectedListId: string | null | undefined;
  /**
   * Whether the popover is showing. Owned by the dialog rather than here
   * because its Escape handler has to know an inline popover is open before
   * deciding whether Escape should close the whole dialog.
   */
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelectList: (list: List) => void;
  onClearList: () => void;
  onAddList: () => void;
  /** Shared lists (#286), ADR-0003: a Member may only move/clear a Task
   * they authored. Set by the caller — undefined (the default) never
   * disables anything, matching every existing usage that predates
   * sharing. */
  disabledReason?: string | null;
}

/**
 * The list control in the dialog's metadata column: the trigger carrying the
 * current list's emoji and name, the button that clears the assignment, and
 * the popover they open.
 */
const ListPicker: React.FC<ListPickerProps> = ({
  lists,
  selectedListId,
  isOpen,
  onToggle,
  onClose,
  onSelectList,
  onClearList,
  onAddList,
  disabledReason = null,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const activeList = lists.find((l) => l.id === selectedListId);
  const { data: sharedListIds } = useSharedListIds();
  const isDisabled = !!disabledReason;

  const trigger = (
    <button
      disabled={isDisabled}
      onClick={(e) => {
        setAnchorEl(e.currentTarget);
        onToggle();
      }}
      className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-soft text-sm transition-all duration-150 ease-in-out ${
        isDisabled ? "opacity-50 cursor-not-allowed" : ""
      } ${
        activeList
          ? "bg-warm-blue text-white shadow-soft-sm"
          : "bg-warm-bg-elevated border border-warm-border text-warm-text-tertiary hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
      }`}
    >
      <span className="text-base leading-none">
        {activeList?.emoji ?? DEFAULT_LIST_EMOJI}
      </span>
      <span className="flex-1 text-left truncate">
        {activeList?.name ?? "Set list"}
      </span>
      {activeList && sharedListIds?.has(activeList.id) && <SharedListMark size={12} />}
      <ChevronDown size={14} className="flex-shrink-0 opacity-70" />
    </button>
  );

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
        List
      </label>
      <div className="flex items-center gap-2">
        {disabledReason ? <Tooltip content={disabledReason}>{trigger}</Tooltip> : trigger}
        {activeList && !isDisabled && (
          <button
            type="button"
            onClick={onClearList}
            aria-label="Remove from list"
            className="flex-shrink-0 p-2 rounded-soft text-warm-text-tertiary hover:text-warm-text-primary hover:bg-warm-bg-elevated transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <AnimatePresence popover>
        {isOpen && !isDisabled && (
          <ListPopover
            lists={lists}
            selectedListId={selectedListId}
            onSelectList={onSelectList}
            onClearList={onClearList}
            onAddList={onAddList}
            onClose={onClose}
            anchorEl={anchorEl}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ListPicker;
